import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteHeader from '../../components/SiteHeader/SiteHeader';
import ContactCenterSection from '../../components/landing/ContactCenterSection';
import styles from './BecasPage.module.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const PROGRAM_ENDPOINTS = [
  { level: 'Preparatoria', endpoint: '/bachillerato/list' },
  { level: 'TSU', endpoint: '/tsu/list' },
  { level: 'Licenciatura', endpoint: '/degrees/list' },
  { level: 'Maestría', endpoint: '/master-degrees/list' },
  { level: 'Especialidad', endpoint: '/specialities/list' },
];

const parseMoney = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const amount = Number(String(value || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
};

const formatMXN = (value) => Number(value).toLocaleString('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const scholarshipPrice = (tuition, discount) => tuition * (1 - discount / 100);

function BecasPage() {
  const navigate = useNavigate();
  const [prices, setPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [pricesError, setPricesError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    Promise.all(PROGRAM_ENDPOINTS.map(async ({ level, endpoint }) => {
      const response = await fetch(`${API_URL}${endpoint}`, {
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`No se pudo consultar ${endpoint}`);
      const payload = await response.json();
      return (Array.isArray(payload.data) ? payload.data : []).map((program) => ({
        ...program,
        level,
      }));
    }))
      .then((groups) => {
        setPrices(groups.flat().filter((program) => parseMoney(program.costs?.tuition) > 0));
        setPricesError('');
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setPricesError('No pudimos consultar los precios vigentes. Intenta nuevamente.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setPricesLoading(false);
      });

    return () => controller.abort();
  }, []);

  return (
    <div className={styles.page}>
      <SiteHeader />

      {/* ── Hero ── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroLeft}>
            <p className={styles.heroEyebrow}>ADMISIONES Y BECAS</p>
            <h1 className={styles.heroTitle}>
              Obtén una beca descuento<br />
              de <span className={styles.heroAccent}>65% OFF</span> en tu<br />
              colegiatura
            </h1>
          </div>
          <div className={styles.heroRight}>
            <img src="/ab/01.png" alt="Estudiante ODM" className={styles.heroImg} />
          </div>
        </div>
      </section>

      {/* ── Tabla de precios ── */}
      <section className={styles.tabla}>
        <div className={styles.tablaInner}>
          <h2 className={styles.tablaTitle}>Precio de colegiatura mensual</h2>
          <p className={styles.tablaSubtitle}>CALCULA TU BECA</p>
          <p className={styles.tablaDesc}>
            Las becas aplican sobre la colegiatura mensual y se asignan al momento de inscripción según la modalidad
            elegida. La beca se mantiene activa mientras cumplas con tus pagos mensuales.{' '}
            <strong>NO TENEMOS COSTOS DE INSCRIPCIÓN NI DE REINSCRIPCIÓN</strong>
          </p>

          <div className={styles.tableWrapper}>
            <table className={styles.preciosTable}>
              <thead>
                <tr>
                  <th className={styles.thBeca}>PROGRAMA</th>
                  <th className={styles.thInscripcion}>CUOTA<br />INSCRIPCIÓN</th>
                  <th className={styles.thPreferente}>
                    BECA PREFERENTE<br />
                    <span className={styles.thOff}>65% OFF</span>
                  </th>
                  <th className={styles.thFlexible}>
                    BECA FLEXIBLE<br />
                    <span className={styles.thOff}>40% OFF</span>
                  </th>
                </tr>
                <tr className={styles.descRow}>
                  <td className={styles.tdDescLabel}>DESCRIPCIÓN</td>
                  <td className={styles.tdDescInscripcion}>Sin costos de inscripción<br />ni de reinscripción.</td>
                  <td className={styles.tdDesc}>
                    <ul className={styles.descList}>
                      <li>Se cobra automáticamente con tarjeta de crédito o débito.</li>
                      <li>Sin preocuparte por pagos cada mes.</li>
                    </ul>
                  </td>
                  <td className={styles.tdDesc}>
                    <ul className={styles.descList}>
                      <li>Pago mensual con tarjeta de credito o debito.</li>
                      <li>El pago no es automatizado.</li>
                      
                    </ul>
                  </td>
                </tr>
              </thead>
              <tbody>
                {pricesLoading && (
                  <tr className={styles.dataRow}>
                    <td colSpan="4" className={styles.priceStatus}>Consultando precios vigentes…</td>
                  </tr>
                )}
                {!pricesLoading && pricesError && (
                  <tr className={styles.dataRow}>
                    <td colSpan="4" className={styles.priceStatus}>{pricesError}</td>
                  </tr>
                )}
                {!pricesLoading && !pricesError && prices.map((program) => {
                  const tuition = parseMoney(program.costs?.tuition);
                  const enrollment = parseMoney(program.costs?.enrollment);
                  return (
                    <tr key={program._id} className={styles.dataRow}>
                      <td className={styles.tdNivel}>
                        <span>{program.name}</span>
                        <small>{program.level}</small>
                      </td>
                      <td className={styles.tdInscripcion}>{formatMXN(enrollment)}</td>
                      <td className={styles.tdPrecio}>
                        <span className={styles.precioOriginal}>{formatMXN(tuition)}</span>
                        <span className={styles.precioDescuento}>{formatMXN(scholarshipPrice(tuition, 65))}</span>
                      </td>
                      <td className={styles.tdPrecio}>
                        <span className={styles.precioOriginal}>{formatMXN(tuition)}</span>
                        <span className={styles.precioDescuento}>{formatMXN(scholarshipPrice(tuition, 40))}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className={styles.ctaBanner}>
        <div className={styles.ctaBannerInner}>
          <div className={styles.ctaBannerLeft}>
            <img src="/computadora.png" alt="Proceso de inscripción" className={styles.ctaImg} />
          </div>
          <div className={styles.ctaBannerRight}>
            <button
              type="button"
              className={styles.ctaBannerText}
              onClick={() => navigate('/inscripcion')}
            >
              Comienza el proceso<br />
              de inscripción <span className={styles.ctaAqui}>AQUÍ</span>
            </button>
          </div>
        </div>
      </section>

      <ContactCenterSection />
    </div>
  );
}

export default BecasPage;
